/**
 * Condizione unica di visibilita' dell'e-commerce (15 sez. 3 e 4.2): hasCommerce = almeno un negozio non
 * scollegato (anche in analisi), letto con get_commerce_store_labels() (basta inventory.view, 15 D2);
 * hasLegacyWoo = WooCommerce sul sistema vecchio. Cache per org (60 s) con dedup delle richieste in volo:
 * menu, notifiche e card la chiedono insieme senza moltiplicare le letture.
 * Prima della migration la funzione non esiste: si considera "nessun negozio" (chi non ha negozi non
 * vede niente di nuovo).
 */
import { useEffect, useState } from 'react';
import { getCurrentUserOrganizationId } from '../stores/referenceDataCache';
import { computePresence, type CommercePresence } from './overview';
import { fetchLegacyWooActive, fetchStoreLabels } from './queries';

const TTL_MS = 60_000;

const EMPTY: CommercePresence = computePresence([], false);

let cache: { orgId: string; at: number; value: CommercePresence } | null = null;
let inFlight: { orgId: string; promise: Promise<CommercePresence> } | null = null;
const listeners = new Set<(p: CommercePresence) => void>();

async function loadPresence(orgId: string): Promise<CommercePresence> {
  const [labels, legacy] = await Promise.all([fetchStoreLabels(), fetchLegacyWooActive(orgId)]);
  return computePresence(labels.error === null ? labels.data : [], legacy);
}

export async function getCommercePresence(force = false): Promise<CommercePresence> {
  const orgId = await getCurrentUserOrganizationId().catch(() => null);
  if (!orgId) return EMPTY;
  if (!force && cache && cache.orgId === orgId && Date.now() - cache.at < TTL_MS) return cache.value;
  if (inFlight && inFlight.orgId === orgId) return inFlight.promise;
  const promise = loadPresence(orgId)
    .catch(() => EMPTY)
    .then((value) => {
      cache = { orgId, at: Date.now(), value };
      for (const l of listeners) l(value);
      return value;
    })
    .finally(() => {
      inFlight = null;
    });
  inFlight = { orgId, promise };
  return promise;
}

/** Da chiamare dopo collegamento o scollegamento: menu e card si aggiornano subito. */
export function invalidateCommercePresence(): void {
  cache = null;
  void getCommercePresence(true);
}

export function useCommercePresence(): CommercePresence & { loading: boolean } {
  const [value, setValue] = useState<CommercePresence | null>(cache?.value ?? null);

  useEffect(() => {
    let alive = true;
    const l = (p: CommercePresence) => alive && setValue(p);
    listeners.add(l);
    void getCommercePresence().then(l);
    return () => {
      alive = false;
      listeners.delete(l);
    };
  }, []);

  return { ...(value ?? EMPTY), loading: value === null };
}
